import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  ShieldAlert, Brain, Bot, Wifi, ArrowUpDown, Bell, BellRing, CheckCheck,
} from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';

const getNotificationIcon = (type: string) => {
  if (type.includes('circuit') || type.includes('drawdown') || type === 'alert')
    return { icon: ShieldAlert, color: 'text-[hsl(var(--accent-red))]' };
  if (type.includes('signal') || type.includes('ai'))
    return { icon: Brain, color: 'text-[hsl(var(--accent-purple))]' };
  if (type.includes('strategy') || type.includes('bot'))
    return { icon: Bot, color: 'text-[hsl(var(--accent-blue))]' };
  if (type.includes('broker') || type.includes('token'))
    return { icon: Wifi, color: 'text-[hsl(var(--accent-amber))]' };
  if (type.includes('trade') || type.includes('order'))
    return { icon: ArrowUpDown, color: 'text-profit' };
  return { icon: Bell, color: 'text-muted-foreground' };
};

const NotificationCenter = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, notifFlash, markAsRead, markAllRead } = useNotifications();

  const handleClick = async (notif: { id: string; action_url: string | null; read: boolean | null }) => {
    if (!notif.read) await markAsRead(notif.id);
    if (notif.action_url) navigate(notif.action_url);
  };

  return (
    <div className={`rounded-xl bg-[hsl(var(--background-secondary))] border border-[hsl(var(--card-border))] transition-all ${
      notifFlash ? 'border-[hsl(var(--accent-blue))] shadow-[0_0_12px_hsl(var(--accent-blue)/0.2)]' : ''
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--card-border))]">
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-semibold font-heading">Notifications</span>
          {unreadCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-[hsl(var(--accent-red))] text-[9px] font-bold text-white flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllRead} className="text-[10px] text-[hsl(var(--accent-blue))] hover:underline flex items-center gap-1">
            <CheckCheck className="h-3 w-3" /> Mark all read
          </button>
        )}
      </div>

      {/* List */}
      {notifications.length > 0 ? (
        <div className="divide-y divide-[hsl(var(--card-border)/0.5)] max-h-[280px] overflow-y-auto">
          {notifications.map((notif) => {
            const { icon: NIcon, color } = getNotificationIcon(notif.type);
            return (
              <button
                key={notif.id}
                onClick={() => handleClick(notif)}
                className={`w-full flex items-start gap-2.5 px-4 py-2.5 text-left hover:bg-[hsl(var(--background-tertiary))] transition-colors ${
                  !notif.read ? 'border-l-2 border-l-[hsl(var(--accent-blue))]' : ''
                }`}
              >
                <NIcon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${color}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-[11px] leading-tight truncate ${!notif.read ? 'font-medium' : 'text-muted-foreground'}`}>
                    {notif.title}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">{notif.message}</p>
                  {notif.created_at && (
                    <span className="text-[9px] text-muted-foreground">
                      {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center py-6 text-muted-foreground">
          <Bell className="h-5 w-5 mb-1.5 opacity-40" />
          <span className="text-xs">No notifications yet</span>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-2 border-t border-[hsl(var(--card-border))]">
        <span className="text-[10px] text-muted-foreground cursor-pointer hover:underline">View all</span>
      </div>
    </div>
  );
};

export default NotificationCenter;
